import { useEffect, useState } from "react";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { ProductPicker } from "../../components/ProductPicker";
import { useConfirm } from "../../components/ConfirmDialog";
import { firstProblem, minLen, isPositiveAmount, isNonNegative } from "../../components/validators";
import type { EngineOverviewDto, MlAccountResult, PublicationAction, ProductCatalogItem, MlQuestionDraft } from "../../ipc/types";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

function MercadoLibrePanel({ onImported, onListingsImported }: { onImported: () => void; onListingsImported: () => void }): JSX.Element {
  const client = useIpc();
  const [account, setAccount] = useState<MlAccountResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);

  const sync = async (): Promise<void> => {
    setBusy(true);
    const res = await client.getMercadoLibreAccount();
    setBusy(false);
    setAccount(res.ok ? res.value : { connected: false, detail: res.error.message });
  };
  const importOrders = async (): Promise<void> => {
    setBusy(true); setImportMsg(null);
    const res = await client.importMlOrders();
    setBusy(false);
    setImportMsg(res.ok ? res.value.message ?? "Sin resultado" : res.error.message);
    if (res.ok && res.value.ok) onImported();
  };
  const importListings = async (): Promise<void> => {
    setBusy(true); setImportMsg(null);
    const res = await client.importMlListings();
    setBusy(false);
    setImportMsg(res.ok ? res.value.message ?? "Sin resultado" : res.error.message);
    if (res.ok && res.value.ok) onListingsImported();
  };

  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name="marketplace" size={18} /> Conexión con Mercado Libre</span>}
        subtitle="Sincroniza tu cuenta e importa tus órdenes reales usando las credenciales de Seguridad"
        action={account ? <Badge tone={account.connected ? "success" : "warning"} dot>{account.connected ? "Conectado" : "Sin conexión"}</Badge> : null}
      />
      <CardBody>
        <div className="row between wrap" style={{ gap: "var(--sp-4)" }}>
          <div className="muted" style={{ fontSize: 13.5, maxWidth: "60ch" }}>
            {importMsg
              ? importMsg
              : account
                ? account.connected
                  ? <>Cuenta <b className="strong">{account.nickname ?? "—"}</b> · sitio {account.siteId ?? "—"} · <b className="strong">{account.items ?? 0}</b> publicaciones activas.</>
                  : account.detail
                : "Pulsa Sincronizar para conectar con la API real de Mercado Libre (OAuth)."}
          </div>
          <div className="row" style={{ gap: 6 }}>
            <Button size="sm" icon="pulse" variant="secondary" onClick={sync} disabled={busy}>
              {busy ? "…" : "Sincronizar cuenta"}
            </Button>
            <Button size="sm" icon="marketplace" variant="secondary" onClick={importListings} disabled={busy}>
              {busy ? "…" : "Traer publicaciones de ML"}
            </Button>
            <Button size="sm" icon="sales" onClick={importOrders} disabled={busy}>
              {busy ? "Trayendo…" : "Traer órdenes de ML"}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/** Preguntas reales de compradores + borradores redactados por la IA (nunca se envían automáticamente). */
function MercadoLibreQuestionsPanel(): JSX.Element {
  const client = useIpc();
  const [drafts, setDrafts] = useState<readonly MlQuestionDraft[] | null>(null);
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sent, setSent] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { ask, node } = useConfirm();

  const draft = async (): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.draftMlQuestionReplies(5);
    setBusy(false);
    if (!res.ok) { setMsg(res.error.message); return; }
    setDrafts(res.value.drafts);
    setEdited(Object.fromEntries(res.value.drafts.map((d) => [d.id, d.draft])));
    setSent({});
    setMsg(res.value.detail);
  };
  const copy = async (id: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(edited[id] ?? "");
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    } catch { /* clipboard no disponible */ }
  };
  const send = async (d: MlQuestionDraft): Promise<void> => {
    const text = (edited[d.id] ?? "").trim();
    if (text.length === 0) { setMsg("La respuesta no puede estar vacía."); return; }
    const okConfirm = await ask({
      title: "Enviar respuesta a Mercado Libre",
      message: `Se publicará esta respuesta en la pregunta del comprador, visible públicamente en tu publicación:\n\n“${text}”\n\nEsta acción no se puede deshacer. ¿Enviar ahora?`,
      confirmLabel: "Sí, enviar a Mercado Libre",
    });
    if (!okConfirm) return;
    setSendingId(d.id); setMsg(null);
    const res = await client.answerMlQuestion(d.id, text);
    setSendingId(null);
    if (res.ok && res.value.ok) setSent((prev) => ({ ...prev, [d.id]: res.value.detail }));
    else setMsg(res.ok ? res.value.detail : res.error.message);
  };

  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      {node}
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name="ai" size={18} /> Preguntas de compradores</span>}
        subtitle="La IA redacta un borrador con tus datos reales. Lo revisas y editas; se envía a Mercado Libre solo cuando tú confirmas."
        action={<Button size="sm" icon="spark" onClick={draft} disabled={busy}>{busy ? "Redactando…" : "Redactar respuestas con IA"}</Button>}
      />
      <CardBody>
        {msg ? <div className="muted" style={{ fontSize: 13, marginBottom: drafts && drafts.length ? "var(--sp-4)" : 0 }}>{msg}</div> : null}
        {!drafts ? (
          <div className="faint" style={{ fontSize: 13 }}>Pulsa “Redactar respuestas con IA” para traer tus preguntas sin responder y generar borradores.</div>
        ) : drafts.length === 0 ? (
          <div className="faint" style={{ fontSize: 13 }}>No hay preguntas sin responder ahora mismo.</div>
        ) : (
          <div className="col" style={{ gap: "var(--sp-4)" }}>
            {drafts.map((d) => {
              const isSent = sent[d.id] !== undefined;
              return (
                <div key={d.id} style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-3, 12px)", padding: "var(--sp-4)" }}>
                  <div className="row between wrap" style={{ gap: 8, marginBottom: "var(--sp-2)" }}>
                    <div className="strong" style={{ fontSize: 13.5 }}>❝ {d.question} ❞</div>
                    {isSent ? <Badge tone="success" dot>Enviada</Badge> : d.itemId ? <Badge tone="info">{d.itemId}</Badge> : null}
                  </div>
                  <textarea
                    className="pd-input"
                    style={{ width: "100%", minHeight: 92, resize: "vertical", fontFamily: "inherit" }}
                    aria-label={`Borrador de respuesta para ${d.question}`}
                    value={edited[d.id] ?? ""}
                    disabled={isSent}
                    onChange={(e) => setEdited((prev) => ({ ...prev, [d.id]: e.target.value }))}
                  />
                  <div className="row between" style={{ gap: 6, marginTop: "var(--sp-2)" }}>
                    <span className="faint" style={{ fontSize: 12 }}>{isSent ? sent[d.id] : ""}</span>
                    <div className="row" style={{ gap: 6 }}>
                      <Button variant="ghost" size="sm" icon="check" disabled={isSent} onClick={() => copy(d.id)}>
                        {copiedId === d.id ? "¡Copiado!" : "Copiar"}
                      </Button>
                      <Button variant="secondary" size="sm" icon="marketplace" disabled={isSent || sendingId === d.id} onClick={() => send(d)}>
                        {isSent ? "Enviada" : sendingId === d.id ? "Enviando…" : "Enviar a Mercado Libre"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="faint" style={{ fontSize: 12 }}>
              Enviar publica la respuesta en tu pregunta de Mercado Libre (visible al público). Siempre te pedimos confirmación antes.
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Acciones disponibles según el estado mostrado en la fila. */
function actionsFor(statusLabel: string): PublicationAction[] {
  if (statusLabel === "Borrador") return ["publish", "delete"];
  if (statusLabel === "Publicado") return ["pause", "delete"];
  if (statusLabel === "Pausado") return ["resume", "delete"];
  return [];
}
const ACTION_LABEL: Record<PublicationAction, string> = { publish: "Publicar", pause: "Pausar", resume: "Reanudar", delete: "Eliminar" };

function NewPublicationForm({ catalog, resolveSku, onCreate, onCancel, busy }: {
  catalog: readonly ProductCatalogItem[];
  resolveSku: (sku: string) => Promise<{ priceMinor: number; stock: number }>;
  onCreate: (v: { sku: string; title: string; priceMinorUnits: number; stock: number }) => void;
  onCancel: () => void; busy: boolean;
}): JSX.Element {
  const [sku, setSku] = useState("");
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("1");
  const [sourceHint, setSourceHint] = useState<string | null>(null);
  const priceNum = Number(price);
  const stockNum = Number(stock);
  const hint = firstProblem([
    { ok: minLen(sku, 2), msg: "Elige un producto (SKU) del catálogo" },
    { ok: minLen(title, 2), msg: "Ingresa un título" },
    { ok: isPositiveAmount(price), msg: "El precio debe ser un número mayor que 0" },
    { ok: isNonNegative(stock), msg: "El stock debe ser 0 o más" },
  ]);
  const valid = hint === null;
  const onPick = (item: ProductCatalogItem): void => {
    setTitle(item.name);
    void resolveSku(item.sku).then((info) => {
      if (info.priceMinor > 0) setPrice(String(info.priceMinor));
      setStock(String(info.stock));
      setSourceHint(`Precio y stock tomados de Precios/Inventario (disp. ${info.stock}${info.priceMinor > 0 ? "" : "; sin precio en listas"}).`);
    });
  };
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nueva publicación" subtitle="Elige un producto de tu catálogo; se toman precio (Precios) y stock (Inventario) reales" />
      <div className="card__body">
        <div className="pd-form">
          <div className="pd-field"><span className="pd-field__label">SKU</span>
            <ProductPicker catalog={catalog} value={sku} onChange={setSku} ariaLabel="SKU" onPick={onPick} /></div>
          <div className="pd-field"><span className="pd-field__label">Precio (CLP)</span>
            <input className="pd-input" value={price} type="number" min="0" placeholder="Ej: 54990" aria-label="Precio" onChange={(e) => setPrice(e.target.value)} /></div>
          <div className="pd-field"><span className="pd-field__label">Stock</span>
            <input className="pd-input" value={stock} type="number" min="0" aria-label="Stock" onChange={(e) => setStock(e.target.value)} /></div>
          <div className="pd-field pd-form__full"><span className="pd-field__label">Título</span>
            <input className="pd-input" value={title} placeholder="Ej: Aro de luz LED 26cm con trípode" aria-label="Título" onChange={(e) => setTitle(e.target.value)} /></div>
        </div>
        {sourceHint ? <div className="muted" style={{ fontSize: 12.5, marginTop: "var(--sp-2)" }}>{sourceHint}</div> : null}
        <div className="pd-actions">
          {hint ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{hint}</span> : null}
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate({ sku: sku.trim(), title: title.trim(), priceMinorUnits: Math.round(priceNum), stock: Math.round(stockNum) })}>
            {busy ? "Creando…" : "Crear publicación"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function PublicationsPanel(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<EngineOverviewDto>((c) => c.getEngineOverview("marketplace"), []);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<readonly ProductCatalogItem[]>([]);
  const { ask, node } = useConfirm();

  useEffect(() => {
    let active = true;
    client.getProductCatalog().then((res) => { if (active && res.ok) setCatalog(res.value.items); });
    return () => { active = false; };
  }, [client]);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;

  const resolveSku = async (sku: string): Promise<{ priceMinor: number; stock: number }> => {
    const res = await client.getSkuInfo(sku);
    return res.ok ? { priceMinor: res.value.priceMinor, stock: res.value.stock } : { priceMinor: 0, stock: 0 };
  };

  const create = async (v: { sku: string; title: string; priceMinorUnits: number; stock: number }): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.createPublication(v);
    setBusy(false);
    if (res.ok && res.value.ok) { setShowForm(false); setMsg(`Publicación "${v.title}" creada.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo crear" : res.error.message);
  };
  const act = async (id: string, action: PublicationAction): Promise<void> => {
    if (action === "delete") {
      const ok = await ask({ title: "Eliminar publicación", message: "La publicación se eliminará de tu catálogo. Esta acción no se puede deshacer. ¿Continuar?", confirmLabel: "Sí, eliminar", danger: true });
      if (!ok) return;
    }
    setBusy(true); setMsg(null);
    const res = await client.setPublicationStatus(id, action);
    setBusy(false);
    if (res.ok && res.value.ok) reload();
    else setMsg(res.ok ? res.value.message ?? "Acción no permitida" : res.error.message);
  };
  const syncMl = async (id: string): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.syncPublicationToMl(id);
    setBusy(false);
    setMsg(res.ok ? res.value.message ?? (res.value.ok ? "Publicado en Mercado Libre" : "No se pudo publicar") : res.error.message);
  };

  return (
    <div>
      {node}
      <div className="row between wrap" style={{ marginBottom: "var(--sp-4)", gap: "var(--sp-3)" }}>
        <h2 className="strong" style={{ fontSize: 16 }}>Publicaciones</h2>
        <Button size="sm" icon="marketplace" onClick={() => setShowForm((s) => !s)}>Nueva publicación</Button>
      </div>

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.metrics.map((m) => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon as IconName} accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewPublicationForm catalog={catalog} resolveSku={resolveSku} onCreate={create} onCancel={() => setShowForm(false)} busy={busy} /></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Catálogo publicado" subtitle={`${data.rows.length} publicaciones`} action={msg ? <span className="muted" style={{ fontSize: 12.5 }}>{msg}</span> : null} />
        <div className="card__body" style={{ padding: 0 }}>
          {data.rows.length === 0 ? (
            <div className="statecard"><Icon name="marketplace" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Aún no tienes publicaciones en la app</div><div className="faint" style={{ fontSize: 13 }}>Pulsa <b className="strong">“Traer publicaciones de ML”</b> (arriba) para reflejar tus publicaciones reales de Mercado Libre, o crea una nueva con “Nueva publicación”.</div></div>
          ) : (
            <table className="table">
              <thead><tr><th>Publicación</th><th>Detalle</th><th>Estado</th><th>Precio</th><th>Acciones</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td><div className="strong">{r.primary}</div></td>
                    <td className="muted">{r.secondary}</td>
                    <td><Badge tone={r.status.tone} dot>{r.status.label}</Badge></td>
                    <td className="strong">{r.amount}</td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {actionsFor(r.status.label).map((a) => (
                          <Button key={a} variant={a === "delete" ? "ghost" : "secondary"} size="sm" disabled={busy} onClick={() => act(r.id, a)}>{ACTION_LABEL[a]}</Button>
                        ))}
                        {r.status.label === "Publicado" ? (
                          <Button variant="ghost" size="sm" icon="pulse" disabled={busy} onClick={() => syncMl(r.id)}>Sincronizar ML</Button>
                        ) : null}
                        {actionsFor(r.status.label).length === 0 ? <span className="faint" style={{ fontSize: 12 }}>—</span> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

export function MarketplaceScreen(): JSX.Element {
  // Al importar publicaciones de ML, se refresca el catálogo remontando el panel.
  const [catalogNonce, setCatalogNonce] = useState(0);
  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="marketplace" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Marketplace</h1>
              <p className="pagehead__sub">Publicaciones, cuentas y sincronización con Mercado Libre.</p>
            </div>
          </div>
        </div>
      </div>
      <MercadoLibrePanel
        onImported={() => { /* las órdenes importadas se ven en Ventas/Inventario/Finanzas/Logística */ }}
        onListingsImported={() => setCatalogNonce((n) => n + 1)}
      />
      <MercadoLibreQuestionsPanel />
      <PublicationsPanel key={catalogNonce} />
    </div>
  );
}
